import clsx from 'clsx'
import { GetStaticPropsContext } from 'next'
import { SWRConfig } from 'swr'
import { BaseDataType, FlchPages } from '../@types/statamic'
import { PageProps } from '../@types/util'
import CookieBanner from '../components/elements/CookieBanner'
import FaqJsonLd from '../components/elements/FaqJsonLd'
import FlchBaseData from '../graphql/FlchBaseData.graphql'
import FlchPageQuery from '../graphql/FlchPageQuery.graphql'
import { filterNavs } from '../lib/filterNavs'
import {
  getBlockDescription,
  getBlockImage,
  getBlockTitle,
  loadBlockProps,
  renderBlock,
} from '../lib/PageBuilder'
import { batchRequests, statamicConfig } from '../StatamicGraphQLClient'
import { PageParams, PreviewData } from '@/src/@types/infrastructur'
import { buildBreadcrumbs } from '@/src/components/flch/breadcrumbs'
import BreadcrumbListJsonLd from '@/src/components/flch/components/BreadcrumbListJsonLd'
import BaseLayout from '@/src/components/flch/navigation/BaseLayout'
import MetaTags from '@/src/components/MetaTags'
import { ensureTrailingSlash } from '@/src/lib/str'

const BASE_URL = process.env.BASE_URL

type Props = PageProps<typeof getStaticProps>

export async function getStaticProps(
  context: GetStaticPropsContext<PageParams, PreviewData>
) {
  const urlParts = [...(context.params?.uri || [])]
  const currentUri = `/${urlParts.join('/')}`

  const [{ entry }, { navs, settings }] = await batchRequests<
    [FlchPages, BaseDataType]
  >(
    [
      {
        document: FlchPageQuery,
        variables: {
          uri: currentUri,
          locale: `${process.env.NEXT_PUBLIC_STATAMIC_SITE_HANDLE}_de`,
          collections: statamicConfig.collections,
        },
      },
      {
        document: FlchBaseData,
        variables: {
          locale: `${process.env.NEXT_PUBLIC_STATAMIC_SITE_HANDLE}_de`,
          settingsHandle: statamicConfig.settingsHandle,
        },
      },
    ],
    context.previewData?.statamicToken
  )

  const filteredNavs = filterNavs(navs)
  const blocks = await loadBlockProps(entry.flch_page_builder, context)

  if (!entry.social_title) {
    entry.social_title = entry.seo_title
  }
  if (!entry.social_text) {
    entry.social_text = entry.seo_summary
  }

  if (
    !entry.social_title ||
    !entry.seo_summary ||
    !entry.social_text ||
    !entry.social_image
  ) {
    for (const block of blocks) {
      const title = getBlockTitle(block)
      if (title && !entry.social_title) {
        entry.social_title = title
      }

      const text = getBlockDescription(block)
      if (text && !entry.social_text) {
        entry.social_text = text
      }
      if (text && !entry.seo_summary) {
        entry.seo_summary = text
      }

      const image = getBlockImage(block)
      if (image && !entry.social_image) {
        entry.social_image = image
      }

      if (
        entry.social_title &&
        entry.seo_summary &&
        entry.social_text &&
        entry.social_image
      ) {
        break
      }
    }
  }

  const uri = context.params?.uri ? context.params?.uri?.join('/') : ''

  return {
    props: {
      blocks,
      navs: filteredNavs,
      settings,
      seoTitle: entry.seo_title || null,
      seoSummary: entry.seo_summary || null,
      socialTitle: entry.social_title || null,
      socialText: entry.social_text || null,
      socialImage: entry.social_image || null,
      date: entry.date,
      fullUrl: uri ? BASE_URL + '/' + ensureTrailingSlash(uri) : BASE_URL,
      noindex: entry.noindex,
    },
  }
}

export default function Flch({
  blocks,
  navs,
  settings,
  seoTitle,
  seoSummary,
  socialTitle,
  socialText,
  socialImage,
  date,
  fullUrl,
  noindex,
}: Props) {
  return (
    <>
      <MetaTags
        seoTitle={seoTitle}
        seoSummary={seoSummary}
        title={socialTitle}
        description={socialText}
        fullUrl={fullUrl}
        date={date}
        image={socialImage}
        noindex={noindex}
      />
      <CookieBanner />
      <FaqJsonLd blocks={blocks} />
      <BreadcrumbListJsonLd
        breadcrumbs={buildBreadcrumbs(socialTitle || seoTitle || 'Page')}
        fullUrl={fullUrl}
      />
      <BaseLayout navs={navs} settings={settings}>
        <div
          className={clsx('flex flex-1 flex-col', {
            'pb-20 md:pb-32':
              blocks[blocks.length - 1]?.block !== 'flch_contact_local' &&
              blocks[blocks.length - 1]?.block !== 'flch_book_trial_lesson',
          })}
        >
          {blocks.map((block, index) => (
            <div key={index} className="relative flex flex-1 flex-col">
              <span
                id={`${block.block}-${
                  blocks.slice(0, index).filter((b) => b.block === block.block)
                    .length + 1
                }`}
                className="absolute -top-20"
              ></span>
              <SWRConfig value={{ fallback: block.fallback ?? {} }}>
                {renderBlock(block, index, blocks, {
                  settings,
                  socialImage,
                  fullUrl,
                  blueprint: 'pages',
                  newSpacing: blocks.some(
                    (block) => block.block === 'flch_header'
                  ),
                })}
              </SWRConfig>
            </div>
          ))}
        </div>
      </BaseLayout>
    </>
  )
}
